import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

import { ApexOptions } from 'apexcharts';
import styled, { useTheme } from 'styled-components';

interface IBasicRadialBarChart {
	value_name?: string;
	value: number;
	color?: string;
	width?: string;
	height?: string;
	date_picker?: any;
	label_value_size?: string;
	label_value_weight?: string;
	label_value_family?: string;
	offsetY?: number;
	data_label?: boolean;
	hollow_size?: string;
}

// BasicRadialBarChart: 원형 바 차트 컴포넌트

/**
 * @description 기본 방사형 막대 차트를 렌더링하는 컴포넌트입니다.
 *
 * @param {string} value_name - 차트의 값 이름입니다.
 * @param {number} value - 차트에 표시될 값입니다.
 * @param {string} color - 차트의 색상 테마입니다.
 * @param {string | number} width - 차트의 너비입니다.
 * @param {string | number} height - 차트의 높이입니다.
 * @param {boolean} date_picker - 날짜 선택기 사용 여부입니다.
 * @param {string} label_value_size - 차트 값 레이블의 폰트 크기입니다.
 * @param {string} label_value_weight - 차트 값 레이블의 폰트 두께입니다.
 * @param {string} label_value_family - 차트 값 레이블의 폰트 패밀리입니다.
 * @param {number} offsetY - 차트 값 레이블의 Y축 오프셋입니다.
 */
const BasicRadialBarChart = ({
	value_name,
	value,
	color,
	width,
	height,
	date_picker,
	label_value_size,
	label_value_weight,
	label_value_family,
	offsetY,
	data_label,
	hollow_size,
}: IBasicRadialBarChart) => {
	const theme = useTheme();
	const [Chart, setChart] = useState<{
		series: any[];
		options: ApexOptions;
	}>({
		series: [value?.toString() == 'NaN' ? '??' : value],
		options: {
			labels: [],
			fill: {
				type: 'gradient',
				colors: [theme.colors.uplus.primary50_key],
				gradient: {
					shade: 'dark',
					type: 'vertical',
					inverseColors: false,
					gradientToColors:
						color === 'dark' ? ['#3a1d91'] : [theme.colors.uplus.primary50_key],
					stops: [0],
				},
			},
			plotOptions: {
				radialBar: {
					offsetY: 0,
					hollow: {
						size: hollow_size ?? '45%',
					},
					dataLabels: {
						show: data_label ?? true,
						name: {
							show: false,
							color: 'gray',
							fontSize: '12px',
						},
						value: {
							show: true,
							fontSize: '15px',
							offsetY: 5,
						},
					},
				},
			},
		},
	});

	useEffect(() => {
		setChart({
			series: [value?.toString() == 'NaN' ? '??' : value],
			options: {
				labels: [],
				fill: {
					type: 'gradient',
					colors: [theme.colors.uplus.primary50_key],
					gradient: {
						shade: 'dark',
						type: 'vertical',
						inverseColors: false,
						gradientToColors:
							color === 'dark' ? ['#3a1d91'] : [theme.colors.uplus.primary50_key],
						stops: [0],
					},
				},
				plotOptions: {
					radialBar: {
						offsetY: 0,
						hollow: {
							size: hollow_size ?? '45%',
						},
						dataLabels: {
							show: data_label ?? true,
							name: {
								show: false,
								color: 'gray',
								fontSize: '12px',
							},
							value: {
								show: true,
								fontSize: label_value_size ?? '15px',
								fontFamily: label_value_family ?? '',
								fontWeight: label_value_weight ?? 'normal',
								offsetY: offsetY ?? 5,
							},
						},
					},
				},
			},
		});
	}, [value]);

	return (
		<ChartBox>
			<ReactApexChart
				options={Chart?.options}
				series={Chart?.series}
				type="radialBar"
				width={width ?? '100%'}
				height={height ?? '40%'}
			/>
			{value_name && (
				<ChartTitle>{value_name == 'undefined' ? '분석중' : value_name}</ChartTitle>
			)}
		</ChartBox>
	);
};

const ChartBox = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	justify-content: center;
	gap: 10px;
`;

const ChartTitle = styled.div`
	font-weight: 300;
	font-size: 12px;
	line-height: 21px;
	text-align: center;
	position: relative;
	top: -30%;
`;

const SectionPadding = styled.div<{ padding: string }>`
	padding: ${({ padding }) => padding};
`;

export default BasicRadialBarChart;
